import { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Modal, Text, useMantineTheme } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Calendar } from "iconsax-react";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import moment from "moment";
import { useMediaQuery } from "@mantine/hooks";
import { IQueryPosition } from "../../../../interfaces/IQuery";
import { useTranslation } from "react-i18next";
import { convertToDate, countdownDate } from "../../../../utils/date";
import { useAuth } from "../../../../context/auth";

interface IProps {
  query: IQueryPosition;
  setQuery: React.Dispatch<React.SetStateAction<IQueryPosition>>;
}

export default function ModalRangeDate({ query, setQuery }: IProps) {
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const isLargerThan780 = useMediaQuery("(min-width: 780px)");
  const { user } = useAuth();
  const userCreatedAt = user?.created_at;

  const daysSinceUserCreated = useMemo(() => {
    if (!userCreatedAt) return null;
    return countdownDate(userCreatedAt);
  }, [userCreatedAt]);

  const preDefinitionDates = useMemo(() => {
    const base = [
      { label: t("operacoes.data_popover_info_user.last_30_days"), value: 29 },
      { label: t("operacoes.data_popover_info_user.last_7_days"), value: 6 },
      { label: t("operacoes.data_popover_info_user.today"), value: 0 },
    ];
    if (daysSinceUserCreated !== null) {
      return [
        {
          label: t("operacoes.data_popover_info_user.all"),
          value: daysSinceUserCreated,
        },
        ...base,
      ];
    }
    return base;
  }, [daysSinceUserCreated, t]);

  const [opened, setOpened] = useState(false);
  const [tempDate, setTempDate] = useState<[Date | null, Date | null]>([
    query.open_date_start ?? null,
    query.open_date_end ?? null,
  ]);

  const [date, setDate] = useState<[Date | null, Date | null]>([
    query.open_date_start ?? null,
    query.open_date_end ?? null,
  ]);

  useEffect(() => {
    setTempDate([query.open_date_start ?? null, query.open_date_end ?? null]);
    setDate([query.open_date_start ?? null, query.open_date_end ?? null]);
  }, [query.open_date_start, query.open_date_end]);

  const endOfDay = (date: Date) => {
    return moment(date).endOf("day").toDate(); // 23:59:59.999
  };

  const handleSelectPreDefinition = (numberDays: number) => {
    const today = new Date();
    const pastDate = moment(today)
      .subtract(numberDays, "days")
      .startOf("day")
      .toDate();
    const endDate = endOfDay(today);
    setDate([numberDays ? pastDate : today, endDate]);
  };

  const handleContinue = () => {
    const endDate = date[1]
      ? endOfDay(date[1])
      : endOfDay(date[0] ?? new Date());

    setTempDate(date);
    setQuery((prevQuery) => ({
      ...prevQuery,
      open_date_start: date[0] ?? prevQuery.open_date_start,
      open_date_end: endDate,
    }));
    setOpened(false);
  };

  const getButtonLabel = (date: [Date | null, Date | null]): string => {
    if (!date[0]) return t("operacoes.data_popover_info_user.select_dates");

    const startRaw = moment(date[0]);
    const endRaw = date[1] ? moment(date[1]) : null;
    const todayStart = moment().startOf("day");

    // Normaliza para limites do dia para evitar off-by-one com horas vindas do backend
    const startDay = startRaw.startOf("day");
    const endDay = endRaw ? endRaw.endOf("day") : null;

    const startDateFormated = convertToDate(date[0]);
    const endDateFormated = convertToDate(date[1] || undefined);

    if (!endDay)
      return startDay.isSame(todayStart, "day")
        ? t("operacoes.data_popover_info_user.today")
        : startDateFormated || "";

    const diffDays = endDay.diff(startDay, "days");

    if (endDay.isSame(todayStart, "day")) {
      const preDefinedLabel = preDefinitionDates.find(
        (item) => item.value === diffDays
      )?.label;
      if (preDefinedLabel) return preDefinedLabel;
      return t("operacoes.data_popover_info_user.from_to", {
        startDate: startDateFormated,
        endDate: endDateFormated,
      });
    }

    return t("operacoes.data_popover_info_user.from_to", {
      startDate: startDateFormated,
      endDate: endDateFormated,
    });
  };

  const effectiveDateRange: [Date | null, Date | null] = useMemo(() => {
    if (opened) return date;
    return [query.open_date_start ?? null, query.open_date_end ?? null];
  }, [opened, date, query.open_date_start, query.open_date_end]);

  const buttonLabel = getButtonLabel(effectiveDateRange);

  return (
    <>
      <Flex direction={"column"} gap={8} miw="230px">
        <Text fw={600} fz="14px" lh="155%">
          {t("operacoes.data_popover_info_user.label")}
        </Text>
        <Button
          bg={"primaryOpacity.1"}
          color={theme.colors.primary[0]}
          leftIcon={<Calendar size="24" color={theme.colors.primary[0]} />}
          style={{
            width: "fit-content",
            display: "flex",
            gap: "12px",
            borderRadius: "50px",
            fontFamily: "Barlow",
            fontWeight: 500,
            fontSize: "14px",
            whiteSpace: "wrap",
            wordBreak: "break-word",
          }}
          styles={{
            root: {
              "&:hover": {
                backgroundColor: theme.colors.primaryOpacity[1],
                color: theme.colors.other[2],
              },
            },
            inner: {
              color: theme.colors.primary[0],
              fontWeight: 500,
            },
          }}
          onClick={() => {
            setOpened(!opened);
          }}
        >
          {buttonLabel}
        </Button>
      </Flex>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size="auto"
        centered
        withCloseButton={false}
        styles={{
          content: {
            borderRadius: "12px",
          },
          body: {
            padding: "20px",
          },
        }}
      >
        <Flex
          direction={"column"}
          justify={"space-between"}
          w={{ base: "fit-content", 780: "100%" }}
        >
          <Flex
            direction={{ base: "column", 780: "row" }}
            w={{ base: "fit-content", 780: "100%" }}
            h={"315px"}
          >
            <DatePicker
              firstDayOfWeek={0}
              locale={language}
              mb={15}
              type="range"
              value={date}
              onChange={setDate}
              maxDate={new Date()}
              pr="20px"
              sx={{
                borderRight: isLargerThan780
                  ? `1.5px solid  ${theme.colors.basic[2]}`
                  : "none",
              }}
            />
            <Flex
              pl={20}
              direction={"column"}
              align="start"
              justify="start"
              h="auto"
              gap={8}
              w={{ base: "fit-content", 780: "100%" }}
            >
              <Text>
                {t("operacoes.data_popover_info_user.pre_definition")}
              </Text>
              <Box pl={10}>
                {preDefinitionDates.map((item, index) => (
                  <Text
                    key={`preDefinitionDates${index}`}
                    onClick={() => handleSelectPreDefinition(item.value)}
                    sx={{
                      cursor: "pointer",
                      transition: "0.5s text-decoration",
                      ":hover": {
                        cursor: "pointer",
                        textDecoration: "underline",
                        transition: "0.5s text-decoration",
                      },
                    }}
                  >
                    {item.label}
                  </Text>
                ))}
              </Box>
            </Flex>
          </Flex>

          <Flex
            pt={20}
            gap="16px"
            justify={"space-between"}
            align={"center"}
            sx={{ borderTop: `1.5px solid  ${theme.colors.basic[2]}` }}
            direction={{ base: "column", 780: "row" }}
          >
            <Button
              variant="primary"
              h={38}
              onClick={() => {
                setOpened(false);
                setDate(tempDate);
              }}
              w={{ base: "100%", 780: "auto" }}
            >
              {t("global.button.cancel")}
            </Button>
            <Flex gap={"5px"} align={"start"} direction={"column"}>
              <Text>{buttonLabel}</Text>
            </Flex>
            <Button
              variant="primary"
              h={38}
              onClick={handleContinue}
              w={{ base: "100%", 780: "auto" }}
            >
              {t("global.button.continue")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
